import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';
import PublicNavigation from '../../components/ui/PublicNavigation';
import Icon from '../../components/AppIcon';
import { trackAuth } from '../../services/analyticsService';

const DriverLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [debugInfo, setDebugInfo] = useState('');
  const [diagnosticSteps, setDiagnosticSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const addStep = (steps, msg) => {
    console.log('🔍 [DRIVER LOGIN DIAG]', msg);
    return [...steps, msg];
  };

  // ── Log connexion dans login_logs ──────────────────────────────────────────
  const logLoginAttempt = async ({ userId, email: userEmail, success, failureReason }) => {
    try {
      await supabase?.from('login_logs')?.insert({
        user_id: userId || null,
        email: userEmail,
        success,
        failure_reason: failureReason || null,
        ip_address: null, // not accessible from browser
        user_agent: navigator?.userAgent || null,
        login_at: new Date()?.toISOString(),
      });
    } catch (logErr) {
      console.warn('[DRIVER LOGIN] Could not write login_log:', logErr?.message);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setDebugInfo('');
    setDiagnosticSteps([]);
    setLoading(true);

    const trimmedEmail = email?.trim()?.toLowerCase();
    let steps = [];
    let authUserId = null;

    try {
      steps = addStep(steps, `Tentative de connexion pour: ${trimmedEmail}`);
      setDiagnosticSteps([...steps]);

      // ── ÉTAPE 1 : Supabase Auth signInWithPassword ──────────────────────
      console.log('🔐 [DRIVER LOGIN] signInWithPassword →', trimmedEmail);

      const { data, error: authError } = await supabase?.auth?.signInWithPassword({
        email: trimmedEmail,
        password: password,
      });

      console.log('📋 [DRIVER LOGIN] Auth response:', {
        user: data?.user
          ? {
              id: data?.user?.id,
              email: data?.user?.email,
              confirmed: data?.user?.email_confirmed_at,
              banned: data?.user?.banned_until,
            }
          : null,
        session: data?.session ? 'présente' : 'absente',
        error: authError
          ? { message: authError?.message, status: authError?.status, code: authError?.code }
          : null,
      });

      if (authError) {
        console.error('❌ [DRIVER LOGIN] Erreur Auth Supabase:', authError);

        const debugMsg = `Code: ${authError?.code || 'N/A'} | Status: ${authError?.status || 'N/A'} | Message: "${authError?.message}"`;
        setDebugInfo(debugMsg);

        steps = addStep(steps, `❌ Supabase Auth échoué — ${debugMsg}`);
        setDiagnosticSteps([...steps]);

        let errorMsg = authError?.message || "Erreur d'authentification Supabase";

        if (
          authError?.message?.includes('Invalid login credentials') ||
          authError?.message?.includes('invalid_credentials') ||
          authError?.code === 'invalid_credentials' ||
          authError?.status === 400
        ) {
          errorMsg = 'Email ou mot de passe incorrect. Vérifiez vos identifiants ou cliquez sur "Mot de passe oublié".';
        } else if (authError?.message?.includes('Email not confirmed')) {
          errorMsg = "Email non confirmé dans Supabase. Contactez l'administrateur.";
        } else if (authError?.message?.includes('User not found')) {
          errorMsg = "Aucun compte trouvé avec cet email dans Supabase Auth.";
        } else if (authError?.message?.includes('NetworkError') || authError?.message?.includes('fetch')) {
          errorMsg = 'Erreur réseau. Vérifiez votre connexion internet.';
        }

        await logLoginAttempt({ email: trimmedEmail, success: false, failureReason: authError?.message });
        throw new Error(errorMsg);
      }

      if (!data?.user) {
        await logLoginAttempt({ email: trimmedEmail, success: false, failureReason: 'No user returned' });
        throw new Error('Aucun utilisateur retourné par Supabase Auth.');
      }

      authUserId = data?.user?.id;
      steps = addStep(steps, `✅ Auth Supabase OK — User ID: ${authUserId}`);
      setDiagnosticSteps([...steps]);
      console.log('✅ [DRIVER LOGIN] Auth success. User ID:', authUserId);

      // ── ÉTAPE 2 : Recherche employé par auth_user_id ────────────────────
      steps = addStep(steps, `Recherche employé (auth_user_id=${authUserId}, role=chauffeur)`);
      setDiagnosticSteps([...steps]);

      // First check without is_active filter to detect disabled accounts
      const { data: employeeAny, error: empAnyError } = await supabase
        ?.from('employees')
        ?.select('id, name, email, role, is_active, auth_user_id, must_change_password')
        ?.eq('auth_user_id', authUserId)
        ?.eq('role', 'chauffeur')
        ?.limit(1)
        ?.single();

      console.log('📋 [DRIVER LOGIN] Employee lookup (auth_user_id + role):', {
        employee: employeeAny,
        error: empAnyError ? { message: empAnyError?.message, code: empAnyError?.code } : null,
      });

      if (employeeAny) {
        // Found the employee — check is_active
        if (!employeeAny?.is_active) {
          await supabase?.auth?.signOut();
          await logLoginAttempt({ userId: authUserId, email: trimmedEmail, success: false, failureReason: 'Account disabled' });
          steps = addStep(steps, `❌ Compte désactivé pour ${employeeAny?.name}`);
          setDiagnosticSteps([...steps]);
          throw new Error('Votre compte est désactivé.\nVeuillez contacter votre responsable ADT Express.');
        }

        // Account is active — proceed
        steps = addStep(steps, `✅ Employé trouvé et actif: ${employeeAny?.name}`);
        setDiagnosticSteps([...steps]);
        console.log('✅ [DRIVER LOGIN] Employee found and active:', employeeAny?.name);

        // Track successful login
        try {
          const isSandboxed =
            window.location?.hostname?.includes('builtwithrocket.new') ||
            window.location?.hostname?.includes('rocket.new');
          if (!isSandboxed && typeof window.gtag !== 'undefined') {
            trackAuth('login', 'driver');
          }
        } catch (analyticsError) {
          console.warn('Analytics tracking skipped:', analyticsError);
        }

        await logLoginAttempt({ userId: authUserId, email: trimmedEmail, success: true });

        if (employeeAny?.must_change_password) {
          navigate('/change-password', { replace: true });
        } else {
          navigate('/driver-dashboard', { replace: true });
        }
        return;
      }

      // ── Diagnostic étendu si employé non trouvé ──────────────────────────
      const { data: anyEmployee } = await supabase
        ?.from('employees')
        ?.select('id, name, email, role, is_active, auth_user_id')
        ?.eq('auth_user_id', authUserId)
        ?.limit(1)
        ?.single();

      console.log('🔍 [DRIVER LOGIN] Employé sans filtre (auth_user_id seul):', anyEmployee);

      const { data: byEmail } = await supabase
        ?.from('employees')
        ?.select('id, name, email, role, is_active, auth_user_id')
        ?.eq('email', trimmedEmail)
        ?.limit(1)
        ?.single();

      console.log('🔍 [DRIVER LOGIN] Employé par email:', byEmail);

      await supabase?.auth?.signOut();

      if (anyEmployee) {
        if (!anyEmployee?.is_active) {
          await logLoginAttempt({ userId: authUserId, email: trimmedEmail, success: false, failureReason: 'Account disabled' });
          steps = addStep(steps, `❌ Compte désactivé — Rôle: "${anyEmployee?.role}"`);
          setDiagnosticSteps([...steps]);
          throw new Error('Votre compte est désactivé.\nVeuillez contacter votre responsable ADT Express.');
        }
        steps = addStep(steps, `⚠️ Employé trouvé mais non autorisé — Rôle: "${anyEmployee?.role}", Actif: ${anyEmployee?.is_active}`);
        setDiagnosticSteps([...steps]);
        await logLoginAttempt({ userId: authUserId, email: trimmedEmail, success: false, failureReason: `Wrong role: ${anyEmployee?.role}` });
        throw new Error(
          `Compte trouvé mais accès refusé. Rôle: "${anyEmployee.role}", Statut: ${anyEmployee.is_active ? 'Actif' : 'Inactif'}. Contactez l'administrateur.`
        );
      }

      if (byEmail) {
        steps = addStep(steps, `⚠️ Employé trouvé par email mais auth_user_id non lié (actuel: ${byEmail?.auth_user_id || 'NULL'})`);
        setDiagnosticSteps([...steps]);
        await logLoginAttempt({ userId: authUserId, email: trimmedEmail, success: false, failureReason: 'auth_user_id not linked' });
        throw new Error(
          `Employé trouvé par email mais le compte Auth n'est pas lié. Contactez l'administrateur pour lier le compte (page Diagnostic Utilisateurs).`
        );
      }

      steps = addStep(steps, `❌ Aucun employé chauffeur trouvé pour cet utilisateur Auth`);
      setDiagnosticSteps([...steps]);
      await logLoginAttempt({ userId: authUserId, email: trimmedEmail, success: false, failureReason: 'No driver employee found' });
      throw new Error("Compte chauffeur introuvable ou inactif. Contactez l'administrateur.");
    } catch (err) {
      console.error('❌ [DRIVER LOGIN] Final error:', err?.message);
      setError(err?.message || 'Identifiants incorrects');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicNavigation />

      <div className="flex items-center justify-center px-3 sm:px-4 py-8 sm:py-12 lg:py-16">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Truck" size={24} className="text-green-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Connexion Chauffeur</h1>
            <p className="text-sm sm:text-base text-gray-600">Accédez à vos tâches de ramassage</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <div className="flex-1">
                  <p className="text-sm sm:text-base text-red-800 font-medium whitespace-pre-line">{error}</p>
                  {debugInfo && (
                    <p className="text-xs text-red-500 mt-1 font-mono break-all bg-red-100 rounded px-2 py-1">{debugInfo}</p>
                  )}
                  {diagnosticSteps?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {diagnosticSteps?.map((step, i) => (
                        <p key={i} className="text-xs text-red-600 font-mono">{step}</p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Info box — Mot de passe oublié */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-start gap-2">
            <Icon name="Info" size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700">
              <strong>Vous avez oublié votre mot de passe ?</strong><br />
              Cliquez sur <strong>"Mot de passe oublié"</strong> pour recevoir un lien de réinitialisation.
            </p>
          </div>

          {/* Login Form */}
          <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  placeholder="chauffeur@adtexpress.cloud"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-gray-900 placeholder:text-gray-400"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mot de passe</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e?.target?.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-gray-900 placeholder:text-gray-400"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Forgot password link */}
              <div className="text-right">
                <Link
                  to="/forgot-password?from=driver"
                  className="text-sm text-green-600 hover:text-green-800 font-medium"
                >
                  Mot de passe oublié ?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors shadow-sm min-h-[44px] text-sm sm:text-base font-medium"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Connexion en cours...</span>
                  </>
                ) : (
                  <>
                    <Icon name="LogIn" size={18} />
                    <span>Se connecter</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Back to Home */}
          <div className="mt-4 sm:mt-6 text-center">
            <Link
              to="/home-tracking"
              className="text-sm sm:text-base text-gray-600 hover:text-gray-900 font-medium inline-flex items-center space-x-2 min-h-[44px] py-2"
            >
              <Icon name="ArrowLeft" size={18} />
              <span>Retour à l'accueil</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverLogin;